import videojs from 'video.js';
import marked from 'marked';

import ModalDialog from '../../components/modal-dialog.js';
import { generateMetadataContainer, generateSpeakerBio, generateLink } from './metadata/metadata-template.js';

import InfoIcon from '../../assets/images/info-white-24dp.svg';
import './metadata.css';

const _modal = new WeakMap();
const _titleEl = new WeakMap();
const _dateEl = new WeakMap();
const _timeEl = new WeakMap();
const _durationEl = new WeakMap();
const _descriptionEl = new WeakMap();
const _compliancePolicyEl = new WeakMap();
const _presentersContainerEl = new WeakMap();
const _supportingLinksContainerEl = new WeakMap();
let localize;

/**
 * The Metadata module.
 * @member {MetadataPlugin} metadata
 * @memberof MediasitePlayer#
 */

/**
 * Configures the metadata visible in various parts of the UI.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class MetadataPlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const modalContentEl = generateMetadataContainer(player);
        _titleEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-title'));
        _dateEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-date'));
        _timeEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-time'));
        _durationEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-duration'));
        _descriptionEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-description'));
        _presentersContainerEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-speaker-list'));
        _supportingLinksContainerEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-links-list'));
        _compliancePolicyEl.set(this, modalContentEl.querySelector('.mediasite-player__metadata-modal-compliance-policy'));

        this.on(player, 'durationchange', this.#updateDuration);

        // Set up modal and title overlay
        const modal = new ModalDialog(player, {
            temporary: false,
            uncloseable: false,
            pauseOnOpen: false,
            description: 'Presentation Metadata',
            displayAsPanel: true,
            content: Array.from(modalContentEl.children)
        });
        modal.addClass('mediasite-player__metadata-modal');
        modal.addClass('mediasite-player__control-bar-modal');
        _modal.set(this, modal);

        player.ready(() => {
            player.layout().addChild(modal);
            player.controlbarMenu().addMenuItem(localize('Info'), { id: this.name, icon: InfoIcon, callback: () => modal.open() });
        });
    }

    /** 
     * The title of the current source media.<br><br>Note: also automatically sets the title displayed on the play cover. 
     * @type {string}
     * @see {@link PlaycoverPlugin#title}
     * @example
     * player.metadata.title = 'This is the presentation title';
     */
    set title(title) {
        _titleEl.get(this).textContent = title;
        this.player.playcover().title = title;
    }
    get title() {
        return _titleEl.get(this).textContent;
    }

    /** 
     * The date/time associated with the source media.
     * @type {number}
     * @example
     * player.metadata.datetime = Date.now();
     */
    set datetime(datetime = Date.now()) {
        const dateEl = _dateEl.get(this);
        const timeEl = _timeEl.get(this);
        const date = new Date(datetime);
        dateEl.dataset.value = datetime;
        dateEl.textContent = date.toLocaleDateString();
        timeEl.textContent = date.toLocaleTimeString([], { timeStyle: 'short' });
    }
    get datetime() {
        return parseInt(_dateEl.get(this).dataset.value, 10);
    }

    /**
     * Description text that is associated with the source material.
     * @type {string}
     * @example
     * player.metadata.description = 'This is some description text to be displayed in the metadata panel within the player.';
     */
    set description(description = '') {
        const el = _descriptionEl.get(this);
        el.innerHTML = JSON.parse(`"${description.replaceAll('\\n', '<br>')}"`);
        el.dataset.value = description;
        el.hidden = !(description && description.length > 0);
    }
    get description() {
        return _descriptionEl.get(this).dataset.value;
    }

    /**
     * A string of text that will be parsed as Markdown and displayed in the metadata modal dialog.
     * @type {string}
     * @example
     * player.metadata.compliancePolicyMarkdown = 'This *is* **Markdown**';
     */
    set compliancePolicyMarkdown(compliancePolicyMarkdown) {
        const el = _compliancePolicyEl.get(this);
        if (compliancePolicyMarkdown) {
            el.hidden = false;
            el.innerHTML = marked(compliancePolicyMarkdown);
            el.dataset.value = compliancePolicyMarkdown;
            const links = el.querySelectorAll('a');
            links.forEach((link) => link.target = '_blank');
        }
        else {
            el.hidden = true;
        }
    }
    get compliancePolicyMarkdown() {
        return _compliancePolicyEl.get(this).dataset.value;
    }

    /**
     * A list of presenters associated with the current source media.
     * @type {Presenter[]}
     * @example
     * player.metadata.presenters = [{
     *     bioUrl: '/path/to/bio/page/',
     *     imageUrl: '/path/to/image.jpg',
     *     name: 'James T. Kirk'
     * }];
     */
    set presenters(presenters) {
        const presenterContainer = _presentersContainerEl.get(this);
        presenterContainer.innerHTML = '';
        presenterContainer.dataset.value = JSON.stringify(presenters);

        for (let i = 0; i < presenters.length; i++) {
            const presenter = presenters[i];
            const el = generateSpeakerBio(presenter.name, presenter.imageUrl, presenter.bioUrl);
            presenterContainer.appendChild(el);
        }
    }

    get presenters() {
        return JSON.parse(_presentersContainerEl.get(this).dataset.value);
    }

    /**
     * A list of links to display in the metadata modal dialog.
     * @type {SupportingLink[]}
     * @example
     * player.metadata.supportingLinks = [{
     *     title: 'Mediasite',
     *     url: 'https://mediasite.com'
     * }];
     */
    set supportingLinks(supportingLinks) {
        const containerEl = _supportingLinksContainerEl.get(this);
        containerEl.innerHTML = '';
        containerEl.dataset.value = JSON.stringify(supportingLinks);

        for (let i = 0; i < supportingLinks.length; i++) {
            const currentLink = supportingLinks[i];

            const el = generateLink(currentLink.title, currentLink.url);
            containerEl.appendChild(el);
        }
    }

    get supportingLinks() {
        const containerEl = _supportingLinksContainerEl.get(this);
        return containerEl.dataset.value ? JSON.parse(containerEl.dataset.value) : [];
    }

    /**
     * Opens the metadata modal.
     * @example
     * player.metadata.openModal();
     */
    openModal() {
        _modal.get(this).open();
    }

    /**
     * Closes the metadata modal.
     * @example
     * player.metadata.closeModal();
     */
    closeModal() {
        _modal.get(this).close();
    }

    #updateDuration() {
        const isLive = this.player.usingPlugin('live');
        let seconds = this.player.duration();
        if (isLive) {
            seconds = this.player.liveTracker.liveCurrentTime();

            if (seconds === Infinity) {
                seconds = 0;
            }

            setTimeout(() => this.#updateDuration(), 1000);
        }

        _durationEl.get(this).textContent = localize('Duration') + `: ${videojs.formatTime(seconds)}`;
    }

    /**
     * A boolean indicating whether or not the date/time of publication should be displayed in the metadata UI.
     * @type {boolean}
     * @example
     * player.metadata.showPublishDateTime = true;
     */
    get showPublishDateTime() {
        const dateEl = _dateEl.get(this);
        const timeEl = _timeEl.get(this);
        return !(dateEl.classList.contains('vjs-hidden') || timeEl.classList.contains('vjs-hidden'));
    }

    set showPublishDateTime(showPublishDateTime) {
        const dateEl = _dateEl.get(this);
        const timeEl = _timeEl.get(this);

        if (showPublishDateTime) {
            dateEl.classList.remove('vjs-hidden');
            timeEl.classList.remove('vjs-hidden');
        }
        else {
            dateEl.classList.add('vjs-hidden');
            timeEl.classList.add('vjs-hidden');
        }
    }

    /**
     * A boolean indicating whether or not the Info button should be shown in the controlbar menu.
     * @type {boolean}
     */
    get showControlBarButton() {
        return this.player.controlbarMenu().isMenuItemEnabled(this.name);
    }

    set showControlBarButton(showControlBarButton) {
        if (showControlBarButton) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
        }
    }

    dispose() {
        const modal = _modal.get(this);
        modal.dispose();

        this.player?.removeChild(modal);

        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }

        super.dispose();
    }
}

export default MetadataPlugin;

/**
 * @typedef {Object} Presenter
 * @alias Presenter
 * @memberof MetadataPlugin
 * @property {string} bioUrl
 * @property {string} imageUrl
 * @property {string} name
 */

/**
 * @typedef {Object} SupportingLink
 * @alias SupportingLink
 * @memberof MetadataPlugin
 * @property {string} title
 * @property {string} url
 */
